/**
 * The correspondence panel's React Query definitions (#1069 Story 6.3, NFR62).
 *
 * ⚠️ WHY THE THREAD LIST MOVED HERE. `useCorrespondence` kept the list in
 * `useState` behind a hand-rolled in-flight coalescer: three refs, a `finally`
 * block and an effect that pointed one ref at the latest callback. All of it
 * was a copy of what the library already does — keying, caching, dedupe of
 * concurrent fetches — written because this directory's suites could not
 * mount a `QueryClientProvider`. Story 6.1 removed that constraint
 * (`src/test-utils/render`), so the copy has no reason left to exist.
 *
 * ⚠️ IT LIVES BESIDE THE PANEL, NOT IN `src/hooks/queries/`. The sibling
 * summary hook's own header records that its placement there is a choice and
 * not a constraint (`useCorrespondenceSummary.ts:11-18`). This query is the
 * panel's and nothing outside this directory names its key, so it is
 * colocated; Story 6.4 adds the message pages beside it.
 *
 * ⚠️ THIS IS THE REPO'S FIRST `queryOptions()` (measured 2026-09-16: zero uses
 * under `src/`). It is used because three call sites name this key — the
 * `useQuery` that fetches it, the `setQueryData` that clears a read dot on a
 * row, and the `getQueryState` that reads `fetchStatus` for the coalesce — and
 * a hand-typed key literal at each is exactly how `useRoleManagement.ts:132,147`
 * came to hold two keys that had drifted apart. One typed object, three
 * readers. It is not presented as house style; the 17 existing key factories
 * are, and `correspondenceThreadsKeys` follows them.
 *
 * ⚠️ THE PROVIDER IS A PARAMETER, AND MUST NEVER BE FETCHED HERE.
 * `getMessagingProvider()` is called once in the hook and the suites replace
 * METHODS ON THAT SAME OBJECT in place. A `queryFn` that captured
 * `provider.listThreads` at module load would call the original method for the
 * rest of the process; reading `args.provider.listThreads` at fetch time is
 * what lets a test swap the stub between renders.
 *
 * ⚠️ THE KEY CARRIES THE VIEWER (Story 2.3, C6; Story 6.3, C4). `unread` on a
 * row is per person, so an impersonation start or stop must re-list under the
 * new person's read state. A query whose key does not change does not refetch
 * on an argument change, and `staleTime: 0` refetches on mount and on
 * `enabled` false→true — not on this. The epic's proposed four-segment key
 * would have silently shown one person's read state to another.
 *
 * ⚠️ AND IT DELIBERATELY DOES NOT CARRY THE MEMBER LIST. The old dedupe key
 * was `${requestId}|${space}|${memberKey}`, so a team change re-listed. The
 * answer does not depend on the client's copy of the membership: the server
 * projects membership inside `listThreads` and ignores anything the client
 * sends. `memberKey` was in that key because the callback depended on it, and
 * the callback depended on it because a reconciler that has since been deleted
 * read it. Nothing pinned it.
 *
 * ⚠️ THE LESSON THAT `memberKey`'s DOCBLOCK CARRIED, KEPT BECAUSE IT STILL
 * APPLIES: `members` is rebuilt on every render, so depending on the ARRAY
 * made the reload callback a new function each time, which re-fired its
 * effect, which set state, which re-rendered. That loop issued ~2,600 requests
 * to the vendor in seconds before it was caught. Depend on contents, never on
 * identity — a query key is a list of contents, which is why the loop cannot
 * come back through this door.
 *
 * The options, each with its reason:
 *
 * - `staleTime: 0` — a tab press must re-list. NOT the summary's 30 s: that
 *   hook answers a badge, this one answers the list the user is looking at,
 *   and `useCorrespondence.test.ts` pins "two presses buy exactly two
 *   listings".
 * - `networkMode: 'always'` — the offline-first rule of the house, and the
 *   six existing query precedents (`useUserProfile.ts:55`,
 *   `useLocationTags.ts:40`, `useRequestsQuery.ts:59`, `useFeedbackQuery.ts:29`,
 *   `useOfflineRequestsQuery.ts:59,114`). A failure must reach the panel's
 *   banner as a failure, not sit `paused` with no explanation.
 * - `retry: false` — today a failed listing surfaces AT ONCE and empties the
 *   rows. The app client's default retries a non-4xx twice with backoff
 *   (`src/lib/queryClient.ts:121-135`), which would leave the panel showing
 *   the previous space's rows for seconds with no banner. Precedents:
 *   `useCorrespondenceSummary.ts:168`, `useRequestsQuery.ts:58`.
 * - `refetchOnWindowFocus: false` and `refetchOnReconnect: false` — the panel
 *   has exactly one clock, and it is the summary's (`FR69`). A focus or
 *   reconnect refetch here would issue a listing BESIDE the summary's own
 *   reconnect tick, and the rate-limit arithmetic at
 *   `useCorrespondenceSummary.ts:35-49` counts four limited requests per clock
 *   move already.
 * - NO `refetchInterval` — same reason, stated as a prohibition because it is
 *   the tempting one: the clock belongs to the summary hook and a second
 *   poller would change that arithmetic.
 * - `meta.errorMessage: undefined` — the panel owns its banner and formats the
 *   provider's text itself; the global toast path must not also speak.
 */
import {
  infiniteQueryOptions,
  mutationOptions,
  queryOptions,
} from '@tanstack/react-query';

import type {
  Message,
  PendingMessage,
  SpaceId,
  Thread,
} from './correspondence.types';
import type { IMessagingProvider } from './providers';
import type { OutgoingMessageFile, Page } from './providers/provider.types';
import type { UploadOverlay } from './uploadOverlay';
import type { InfiniteData, Mutation } from '@tanstack/react-query';

export const correspondenceThreadsKeys = {
  /**
   * A sibling of `correspondenceSummaryKeys.all` (`['correspondence',
   * 'summary']`) under the same root, so a future root-prefix invalidation
   * reaches both and neither prefix ever matches the other's entries.
   */
  all: ['correspondence', 'threads'] as const,
  list: (requestId: string, space: SpaceId, viewerId: string | null) =>
    [...correspondenceThreadsKeys.all, requestId, space, viewerId] as const,
};

/**
 * Whether a previous thread-list query's key may lend its rows to this one as
 * placeholder data: same root, same request, same viewer, ANY space.
 *
 * ⚠️ IT LIVES HERE BECAUSE THE KEY'S SHAPE DOES (code review,
 * 2026-09-17). The hook compared `previousQuery.queryKey[2] === requestId &&
 * previousQuery.queryKey[4] === viewerId`. Both literals were right and
 * neither was pinned: the length is asserted in this module's test, but
 * nothing tied those two numbers to the factory, and `queryKey` is
 * `readonly unknown[]`, so `tsc` had nothing to say. One added segment and
 * index 4 becomes `space` -- the viewer check silently becomes a space check,
 * and an impersonation swap inherits the previous person's rows with no test
 * failing. Key arithmetic belongs next to the key.
 *
 * ⚠️ AND THE SPACE'S POSITION IS ASKED OF THE FACTORY, NOT TYPED IN.
 * `SPACE_SEGMENT` is found by building a key from sentinels and locating one,
 * so re-ordering or extending `list()` moves it automatically. A hand-written
 * index here would just be the same bug one module over.
 *
 * `space` is the one segment excluded, because inheriting across a tab press
 * is the entire point (Story 6.3, D3); a request or identity change must not
 * inherit, and those are compared.
 */
const SENTINEL_SPACE = '\u0000space' as SpaceId;
const SPACE_SEGMENT = correspondenceThreadsKeys
  .list('\u0000request', SENTINEL_SPACE, '\u0000viewer')
  .indexOf(SENTINEL_SPACE);

export const listKeyInheritableAcrossSpace = (
  previousKey: readonly unknown[],
  requestId: string,
  viewerId: string | null,
): boolean => {
  const expected = correspondenceThreadsKeys.list(
    requestId,
    previousKey[SPACE_SEGMENT] as SpaceId,
    viewerId,
  );
  return (
    previousKey.length === expected.length &&
    expected.every((segment, i) => previousKey[i] === segment)
  );
};

export const threadsQuery = (args: {
  provider: IMessagingProvider;
  requestId: string;
  space: SpaceId;
  viewerId: string | null;
}) =>
  queryOptions<Thread[]>({
    queryKey: correspondenceThreadsKeys.list(
      args.requestId,
      args.space,
      args.viewerId,
    ),
    // Read off `args.provider` at FETCH time, never destructured above: the
    // suites replace this method on the object the hook holds.
    queryFn: () => args.provider.listThreads(args.requestId, args.space),
    staleTime: 0,
    networkMode: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    meta: {
      // The panel owns its banner; see the header.
      errorMessage: undefined,
    },
  });

// ============================================================================
// The open conversation's message pages (#1069 Story 6.4, NFR62).
// ============================================================================
/**
 * ⚠️ WHY THE PAGES MOVED HERE. The hook kept the open conversation in
 * `useState` behind one effect and two hand-rolled loaders: a cursor ref, an
 * in-flight ref keyed by thread, a generation counter and a merge closure over
 * `State`. The library owns keying, dedupe, the cursor chain and the
 * end-of-thread signal; Story 6.1 removed the constraint that had kept this
 * directory off it, so the copy has no reason left to exist.
 *
 * ⚠️ THE KEY CARRIES THE VIEWER, AND THE SERVER NEVER SEES IT (Story 6.4, C4;
 * the same correction 6.3 made for the list). Thread visibility is per person
 * -- the server refuses `listMessages` to somebody not on the thread -- and
 * today's effect re-pages on the viewer for exactly that reason. A key is a
 * statement about what the ANSWER depends on, not about what the request
 * carries; a key without the viewer keeps one person's pages on screen for
 * another until something else happens to refetch.
 *
 * ⚠️ `gcTime: 0` WAS REJECTED, MEASURED, NOT ASSUMED (C8). The obvious way to
 * make "a thread switch clears the per-thread state" true is to let the entry
 * die. It does not work: `Removable.updateGcTime` is a `Math.max` over every
 * setter (`removable.js:18-22`), and `setQueryData` builds a missing query with
 * the CLIENT's defaults (`queryClient.js:93-104`) -- so the first cache patch
 * raises the entry to 10 minutes in the app and to `Infinity` under the test
 * client, and a reopened thread would show stale pages instantly and re-walk
 * all of them. The hook removes the previous key explicitly instead.
 *
 * ⚠️ THE CLOCK REFRESH FOLDS AND ONLY THE POST RE-WALKS (C7).
 * `infiniteQueryBehavior.js:64-73`: a `refetch` with no direction re-fetches
 * `oldPages.length` pages in sequence. A reader who has pressed "Load earlier"
 * three times has four pages, so an invalidation on every clock move would cost
 * four requests where today costs one -- and would refresh history the Story
 * 2.3 review decided must NOT be marked read. So `refreshOpenThread` keeps its
 * single direct `listMessages` and folds the page in with `foldNewestPage`.
 * `postMessage` has the opposite requirement ("the last-started load wins",
 * which `loadGeneration` bought by hand) and re-walks with
 * `cancelRefetch: true`, which is the library's own version of that rule.
 *
 * ⚠️ A CACHE PATCH MADE DURING A FETCH IS OVERWRITTEN WHEN THAT FETCH LANDS
 * (C6). `onFetch` snapshots `context.state.data?.pages` ONCE, at fetch start
 * (`infiniteQueryBehavior.js:10-13`), and appends every page it fetches to THAT
 * snapshot (:47-49); the settled `setData` replaces the whole `data` with
 * snapshot-plus-page. So an edit applied while "Load earlier messages" is on
 * the wire is silently undone the moment the older page arrives. Every updater
 * below is therefore IDEMPOTENT -- keyed by message id or by page boundary --
 * so the hook's `patch` can apply it now and again after the in-flight fetch
 * settles, which is free when nothing clobbered it and exact when something
 * did.
 *
 * ⚠️ `infiniteQueryOptions()` IS NEW TO THIS REPO (measured 2026-09-17: zero
 * uses under `src/`), as `queryOptions()` was for the threads section. It is
 * used for the same reason: the key, the fetch, the cursor derivation and four
 * separate patch sites all name this query, and a hand-typed key literal at
 * each is exactly how two keys drift apart. It is not presented as house style.
 */
export const correspondenceMessagesKeys = {
  /**
   * A sibling of `correspondenceThreadsKeys.all` under the same root, so
   * neither prefix ever matches the other's entries.
   */
  all: ['correspondence', 'messages'] as const,
  thread: (threadId: string | null, viewerId: string | null) =>
    [...correspondenceMessagesKeys.all, threadId, viewerId] as const,
};

export type MessagesKey = ReturnType<typeof correspondenceMessagesKeys.thread>;

/**
 * Where `thread()` puts the thread id, asked of the factory rather than typed
 * in (code review, 2026-09-17).
 *
 * ⚠️ THE SAME RULE `SPACE_SEGMENT` ABOVE EXISTS FOR, AND FOR THE SAME REASON.
 * The hook's thread-switch effect read `previous[2] === null` to mean "there
 * is no thread under this key, so there is nothing to remove". The literal was
 * right and nothing pinned it: `queryKey` is `readonly unknown[]`, so `tsc`
 * has nothing to say, and one added segment turns index 2 into `viewerId` --
 * at which point the guard inverts and the LIVE entry is removed on a viewer
 * swap. Key arithmetic belongs next to the key.
 */
const SENTINEL_THREAD = '\u0000thread';
const THREAD_SEGMENT = correspondenceMessagesKeys
  .thread(SENTINEL_THREAD, '\u0000viewer')
  .indexOf(SENTINEL_THREAD);

/** Whether a messages key names no thread -- nothing was ever stored under it. */
export const messagesKeyHasNoThread = (key: readonly unknown[]): boolean =>
  key[THREAD_SEGMENT] === null;

/** The cache shape for one conversation: newest page first, each newest-first. */
export type MessagePages = InfiniteData<Page<Message>, string | undefined>;

export const messagesQuery = (args: {
  provider: IMessagingProvider;
  threadId: string | null;
  viewerId: string | null;
}) =>
  infiniteQueryOptions({
    queryKey: correspondenceMessagesKeys.thread(args.threadId, args.viewerId),
    queryFn: ({ pageParam }) => {
      // `enabled` gates the observer; this guards an imperative caller.
      if (args.threadId === null) throw new Error('messagesQuery: no thread');
      // The newest page is asked for EXACTLY as today, with no second
      // argument; an older page hands the cursor back verbatim. The cursor is
      // opaque (`provider.types.ts:96-108`) and is never read here.
      return pageParam === undefined
        ? args.provider.listMessages(args.threadId)
        : args.provider.listMessages(args.threadId, { cursor: pageParam });
    },
    initialPageParam: undefined as string | undefined,
    /**
     * `nextCursor` is the ONLY end signal (`provider.types.ts:114-116`): an
     * empty page WITH a cursor is not the beginning of the conversation, it is
     * a page emptied by withdrawn or invisible messages.
     */
    getNextPageParam: (last: Page<Message>) => last.nextCursor,
    staleTime: 0,
    networkMode: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    meta: {
      // The panel owns its banner; see the threads section's header.
      errorMessage: undefined,
    },
  });

/**
 * The key for one conversation's file listing (#1069 Story 7.4).
 *
 * ⚠️ A SIBLING OF `correspondenceMessagesKeys.all`, NOT A CHILD OF IT. The
 * roots are `['correspondence', 'messages']` and `['correspondence', 'files']`
 * and neither is a prefix of the other, which is what lets the hook invalidate
 * the files of an open thread without touching its message pages — and what
 * the "no root is a prefix of another" pin in `correspondenceQueries.test.ts`
 * exists to keep true.
 *
 * The viewer segment is here for the same reason it is on the messages key: a
 * viewer swap must not serve one person's entitled listing to another.
 */
export const correspondenceThreadFilesKeys = {
  all: ['correspondence', 'files'] as const,
  thread: (threadId: string | null, viewerId: string | null) =>
    [...correspondenceThreadFilesKeys.all, threadId, viewerId] as const,
};

export type ThreadFilesKey = ReturnType<
  typeof correspondenceThreadFilesKeys.thread
>;

/**
 * One conversation's files (#1069 Story 7.4, FR77).
 *
 * ⚠️ THE OPTION BLOCK IS `messagesQuery`'S, VERBATIM, and that is deliberate
 * rather than lazy. `staleTime: 0` is what makes an invalidation BE a refetch
 * while the strip's observer is mounted (D6); `networkMode: 'always'` is what
 * keeps the listing from being paused by `onlineManager` in an app whose
 * whole premise is offline-first; `retry: false` is what makes a refusal cost
 * exactly one call; and `meta.errorMessage: undefined` is what keeps this
 * query out of the global error surface, because a failed listing is silent
 * and its row is simply absent (D8). A divergence in any one of them would be
 * a second policy for the same panel.
 *
 * ⚠️ IT IS NOT LAZY, AND THE STRIP'S OPEN STATE IS. The owner decided
 * (2026-09-17) that the count comes from the listing itself: a control reading
 * "Files (n)" cannot know `n` without the list, and a head request was ruled
 * out. So this is `enabled` exactly as the message pages are, and what the
 * reader's press does is reveal a list already in hand (C1, D1).
 */
export const threadFilesQuery = (args: {
  provider: IMessagingProvider;
  threadId: string | null;
  viewerId: string | null;
}) =>
  queryOptions({
    queryKey: correspondenceThreadFilesKeys.thread(
      args.threadId,
      args.viewerId,
    ),
    queryFn: () => {
      // `enabled` gates the observer; this guards an imperative caller, the
      // same way `messagesQuery`'s does.
      if (args.threadId === null)
        throw new Error('threadFilesQuery: no thread');
      return args.provider.listThreadFiles(args.threadId);
    },
    staleTime: 0,
    networkMode: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    meta: {
      // The panel owns its banner; see the threads section's header. A failed
      // listing says nothing at all (D8).
      errorMessage: undefined,
    },
  });

/**
 * Reading order: oldest first, newest at the bottom (FR66).
 *
 * The provider pages BACKWARDS, so `pages[0]` is the newest page and each
 * page's `items` are newest-first. Flipping both levels here is what the
 * open-thread effect's `[...page.items].reverse()` did, restated over pages.
 */
export const messagesInReadingOrder = (
  data: MessagePages | undefined,
): Message[] => (data ? data.pages.flatMap(p => p.items).reverse() : []);

/**
 * The cursor for messages OLDER than the ones on screen, or `null` when the
 * beginning has been reached. It is read only to decide "is there anything to
 * ask for" -- `getNextPageParam` is what actually builds the request.
 */
export const olderCursorOf = (data: MessagePages | undefined): string | null =>
  data ? data.pages[data.pages.length - 1]?.nextCursor ?? null : null;

/**
 * Fold a freshly fetched NEWEST page in, keeping loaded history (Story 2.3
 * review, group 3). This is `useCorrespondence`'s `mergeNewestPage` restated
 * over `pages`.
 *
 * The newest page in reading order starts at its OLDEST message. If that
 * message is already on screen with history ABOVE it, everything above came in
 * through `loadOlderMessages` and replacing the list would throw it away and
 * put the control back -- so the page replaces the tail instead and the older
 * pages keep only what lies above it. When the page's oldest message is NOT on
 * screen (more than a page of new traffic since the last load), or is the very
 * first thing on screen, the page is the whole truth and replaces everything,
 * cursor included.
 *
 * An EMPTY page has no oldest message and takes the replace branch, exactly as
 * today; the CALLERS guard that case, not the fold.
 *
 * IDEMPOTENT: folding the same page twice reaches the same shape, which is
 * what lets the hook re-apply it after an in-flight fetch lands (C6).
 */
export const foldNewestPage =
  (page: Page<Message>) =>
  (old: MessagePages | undefined): MessagePages => {
    const replaced: MessagePages = {
      pages: [page],
      pageParams: [undefined],
    };
    if (!old || old.pages.length === 0) return replaced;
    // Newest-first, the order the cache holds.
    const newestFirst = old.pages.flatMap(p => p.items);
    const oldestInPage = page.items[page.items.length - 1]?.id;
    if (oldestInPage === undefined) return replaced;
    const at = newestFirst.findIndex(m => m.id === oldestInPage);
    // Not on screen, or the very newest thing on screen: the page is the truth.
    if (at < 0 || at === newestFirst.length - 1) {
      // `at === newestFirst.length - 1` is "the page's oldest message is the
      // OLDEST thing on screen", i.e. index 0 in reading order: nothing above.
      return replaced;
    }
    /**
     * Keep everything STRICTLY older than the page (newest-first: after `at`),
     * page by page, so `pageParams` stays in step. A page emptied by the trim
     * is dropped with its param; the LAST surviving page keeps its
     * `nextCursor`, which is the cursor for what lies above.
     */
    let consumed = 0;
    const pages: Page<Message>[] = [page];
    const pageParams: (string | undefined)[] = [undefined];
    for (let i = 0; i < old.pages.length; i += 1) {
      const p = old.pages[i];
      const start = Math.max(0, at + 1 - consumed);
      consumed += p.items.length;
      if (start >= p.items.length) continue;
      pages.push({ ...p, items: p.items.slice(start) });
      pageParams.push(old.pageParams[i]);
    }
    return { pages, pageParams };
  };

/**
 * A message the viewer just posted, at the NEWEST end (the front of the newest
 * page's newest-first items), which is what "appends below the flipped page"
 * reduces to in reading order.
 *
 * ⚠️ IT MINTS WHEN THE ENTRY IS EMPTY, and the others deliberately do not.
 * That is today's behaviour: a post that resolves before the first page has
 * landed appends to an empty local list and is visible at once. Minting also
 * makes `data` defined, which is what lets the re-fetch's `cancelRefetch: true`
 * actually cancel the held first load rather than dedupe onto it.
 *
 * IDEMPOTENT: an id already present is not appended twice.
 */
export const appendMessage =
  (message: Message) =>
  (old: MessagePages | undefined): MessagePages => {
    if (!old || old.pages.length === 0) {
      return { pages: [{ items: [message] }], pageParams: [undefined] };
    }
    if (old.pages.some(p => p.items.some(m => m.id === message.id))) return old;
    const [newest, ...rest] = old.pages;
    return {
      ...old,
      pages: [{ ...newest, items: [message, ...newest.items] }, ...rest],
    };
  };

/**
 * Map every message in every page -- an edit in place, or a tombstone.
 *
 * ⚠️ `undefined` IN, `undefined` OUT, AND THE `?` IS LOAD-BEARING. An updater
 * that receives `undefined` (the entry was removed on a thread switch, or
 * never existed) must return `undefined`, or `setQueryData` mints a cache entry
 * out of nothing and a later observer reads a conversation no fetch produced.
 * Same rule the thread list's `rows?.map` follows.
 *
 * IDEMPOTENT when `fn` is (both callers map by id to a fixed value).
 */
export const mapMessages =
  (fn: (m: Message) => Message) =>
  (old: MessagePages | undefined): MessagePages | undefined =>
    old && {
      ...old,
      pages: old.pages.map(p => ({ ...p, items: p.items.map(fn) })),
    };

/**
 * Did a newest page cover everything on screen? True when the page is the whole
 * list (its oldest message is the first on screen, or none of it was on screen
 * and it replaces the list); false when older history the reader scrolled up to
 * load stays above it, unrefreshed (review decision 1, 2026-09-11).
 *
 * Moved here unchanged from the hook, so the decision and `foldNewestPage` can
 * be taken from the SAME cache snapshot -- which is what closes the
 * `messagesRef`-vs-queued-state gap the 2026-09-11 second pass recorded.
 */
export const pageCoversScreen = (
  page: Page<Message>,
  onScreen: Message[],
): boolean => {
  const oldestInPage = page.items[page.items.length - 1]?.id;
  if (oldestInPage === undefined) return true;
  return onScreen.findIndex(m => m.id === oldestInPage) <= 0;
};

// ============================================================================
// The four writers (#1069 Story 6.5, NFR62).
// ============================================================================
/**
 * WHY THE WRITE PATH MOVED HERE, and the six things a reader has to know
 * before adding a fifth writer.
 *
 * `createThread`, `postMessage`, `editMessage` and `deleteMessage` were four
 * hand-written `try/catch` blocks over direct provider calls, each with its own
 * `setState`-around-an-`await` sequence. They are four `useMutation`s now, and
 * the mutation cache is the library's record of every correspondence write in
 * flight -- which the test harness's teardown guard already reads
 * (`src/test-utils/render.tsx:165-175`, `client.isMutating()`), so NFR58
 * reaches the writers for the first time.
 *
 * ⚠️ 1. `retry: false`, AND IT IS NOT THE HOUSE DEFAULT. The app client retries
 * a failed mutation ONCE on anything that is not a 4xx
 * (`src/lib/queryClient.ts:152-178`). A POST that REACHED the server, committed
 * and then timed out on the way back is a network failure to the client, and
 * this feature has no idempotency key -- that is a recorded deferral
 * (`deferred-work.md`, "An idempotency key for correspondence writes"), and
 * `.claude/rules/correspondence.md` carries the same rule for the cancel
 * window. Retrying would deliver the message twice, with two copies of every
 * file. What the writers share with the library is its LIFECYCLE, not its
 * retry.
 *
 * ⚠️ 2. `networkMode: 'always'`, AND THE DEFAULT WOULD BE A REPLAY PATH. With
 * the default `'online'` a mutation issued offline does not fail: it sits
 * `isPaused` (`retryer.js:10-11,43`) with its pending row saying "Sending…"
 * indefinitely, and fires BY ITSELF on reconnect. Offline compose and
 * idempotent submission (FR31-FR33) were withdrawn in PRD r2, and NFR52 says
 * any replay path must re-run the whole gate; nothing here may become one by
 * default. Today a post offline fails at once and the draft stays in the
 * composer, which is the behaviour this preserves. Same reason, and the same
 * six precedents, as the two queries above.
 *
 * ⚠️ 3. THE KEY CARRIES NO THREAD ID. A `mutationKey` whose hash changes
 * between renders RESETS the observer (`mutationObserver.js:22-34`), so a key
 * naming the open thread would reset on every thread switch, mid-post. The
 * thread is in the VARIABLES, where it changes per call by design.
 *
 * ⚠️ 4. EVERY SIDE EFFECT GOES IN THE HOOK-LEVEL OPTIONS, NEVER IN PER-CALL
 * `mutate(vars, { ... })` OPTIONS. The panel allows more than one post in
 * flight, and `useMutation` is ONE observer: `observer.mutate()` DETACHES it
 * from the previous mutation before building the next
 * (`mutationObserver.js:56-61`). The previous mutation keeps executing and its
 * hook-level callbacks still run, because they live on the `Mutation`; its
 * per-call callbacks are SKIPPED, because `#notify` fires those only while the
 * observer still holds that call's options (`:74-127`). For the same reason
 * `isPending` describes the LAST call only and is never read.
 *
 * ⚠️ 5. THE CALLBACKS ARE AWAITED BEFORE `mutateAsync` RESOLVES, and the hook
 * depends on it. `mutation.js:123-144` awaits `options.onSuccess` and then
 * `options.onSettled` before it dispatches `success` and before the returned
 * promise resolves (`:150-191` mirrors it for the failure path). That is what
 * keeps `await postMessage()` meaning "the re-walk is done" and lets the
 * existing tests read `messages` synchronously after the await.
 *
 * ⚠️ 6. THE PENDING ROWS ARE THE CACHE'S SINCE STORY 6.6, JOINED IN RENDER.
 * The mutation function's context is `{ client, meta, mutationKey }` and
 * nothing else (`mutation.js:56-64`): no progress channel, no `AbortSignal`,
 * and variables are captured at `mutate()` and never change. So a row is a
 * JOIN: WHICH posts are in flight is the cache's, and
 * `inFlightPostsOptions()` at the foot of this file reads it; HOW FAR ALONG
 * each one is is `uploadOverlay`'s; `toPendingRows()` joins them and keeps the
 * open thread's. The hook holds no copy of either.
 *
 * ⚠️ AND THE OPEN-THREAD FILTER IS APPLIED IN RENDER, NOT IN `filters`.
 * `useMutationState` recomputes only inside its `mutationCache.subscribe`
 * callback, never on a render (`useMutationState.js:20-46`), so a predicate
 * closing over the open thread goes stale; `useMutationStateFilters.test.tsx`
 * measures exactly that on the library and is the reason this is not a style
 * preference. What a user gets out of the move: a pending row survives a
 * thread switch AWAY AND BACK, and survives the docked panel closing and
 * reopening mid-upload.
 *
 * And the one wording rule (Story 6.5, D7): the app-wide
 * `MutationCache.onError` (`queryClient.ts:181-185`) now sees correspondence
 * writes, and toasts for a read-only impersonation refusal exactly as it does
 * for every other write in the product. The hook's `onError` therefore asks
 * `isReadOnlyViolationError` FIRST and shows the SAME sentence in the panel's
 * line, read from `READ_ONLY_VIOLATION_MESSAGE`. A `meta` flag letting a
 * mutation opt out of the global handler is the path to avoid: it would make
 * the read-only rule enforceable per feature.
 *
 * ⚠️ THIS SAID "THE TWO CHANNELS", AND THERE ARE THREE (code review
 * 2026-09-17, decision 2). The panel renders `error` as well, in the
 * "Discussion error" bar (`CorrespondencePanel.tsx:1833-1842`), and it was
 * built from `describe(e)` unconditionally -- so the product's sentence
 * appeared twice and `IMPERSONATION_READ_ONLY_VIOLATION: writes are not
 * allowed` once. `reportWriteFailure` now puts the shared sentence in BOTH
 * slots for this one refusal; every other error still carries the server's
 * text verbatim (AC 37).
 *
 * ⚠️ AND THE RULE REACHED ONLY ONE TRANSPORT (same review, closing C17).
 * `isReadOnlyViolationError` gated on `instanceof ApiError`, but a post with
 * files goes through `postWithProgress.ts`, which throws `UploadError` -- same
 * `status` and `message`, no relation in the type hierarchy -- so a refused
 * FILE post matched nothing at all: no toast, raw code in the panel. The
 * predicate is structural now (403 plus the message, whatever the class), which
 * is why the wording rule holds for both transports rather than for the JSON
 * one only.
 *
 * `mutationOptions()` is new to this repo (measured 2026-09-17: zero uses under
 * `src/`), used for the reason `queryOptions()` above was -- the key, the fn
 * and the three non-default options named once in a typed object the hook
 * spreads, rather than hand-typed at the hook where options drift. The 31
 * `useMutation` hooks under `src/hooks/mutations/` are the house style for the
 * `onMutate`/`onError`/`onSettled` vocabulary, and this follows them; their
 * `cancelQueries` line is the one part deliberately NOT copied (a revert cancel
 * is reported by the test harness, Story 6.4).
 */
export const correspondenceMutationKeys = {
  /** Sibling of the two query roots; never a prefix of either. */
  all: ['correspondence', 'write'] as const,
  createThread: () =>
    [...correspondenceMutationKeys.all, 'createThread'] as const,
  post: () => [...correspondenceMutationKeys.all, 'post'] as const,
  edit: () => [...correspondenceMutationKeys.all, 'edit'] as const,
  delete: () => [...correspondenceMutationKeys.all, 'delete'] as const,
  /** Story 7.3: the fifth writer. No thread id in the key (rule 3 above). */
  renameThread: () =>
    [...correspondenceMutationKeys.all, 'renameThread'] as const,
};

/** What a create carries. */
export interface CreateThreadVariables {
  requestId: string;
  space: SpaceId;
  title: string;
  body: string;
  /** Inline images on the first message (#1530); absent for a text-only one. */
  files?: OutgoingMessageFile[];
}

/**
 * What a post carries. The two callbacks are how the hook keeps the row honest.
 *
 * ⚠️ THE FILE BYTES ARE NOT IN HERE, AND THAT IS THE POINT (#1069 Story 6.5
 * code review, 2026-09-17, decision 1). `variables` is retained by the library
 * for far longer than the call: the `pending` reducer stores it
 * (`mutation.js:217-227`) and NEITHER the `success` nor the `error` reducer
 * clears it (`:228-247`); an attached `Mutation` is not even on a gc clock
 * because `addObserver` calls `clearGcTimeout()` (`:27-36`), and a detached one
 * waits the library default five minutes, because `src/lib/queryClient.ts:119`
 * sets `gcTime` under `queries`, not `mutations`. Worse, and independently of
 * gc, `mutationObserver.js:63-73` spreads `...state` into `#currentResult`, so
 * the observer pins `variables` for the LIFE OF THE HOOK -- and
 * `CorrespondenceLauncher.tsx:86-87` renders the panel unconditionally, so that
 * is the life of the session.
 *
 * A post may carry `MAX_MESSAGE_RAW_CHARS` = 54 MiB of base64
 * (`src/services/correspondence/messageFileBytes.ts:70`), about 108 MB of
 * UTF-16 heap. Before this story those bytes lived only in the in-flight call's
 * closure and were collectable the moment it resolved. So the bytes stay in the
 * hook's `filesRef`, keyed by `localId` and deleted on every exit path, exactly
 * as the abort handle already is; `variables` carries the COUNT, which is all
 * the pending row ever needed.
 *
 * ⚠️ AND IT IS WHAT MADE STORY 6.6 SAFE. 6.6 derives the rows from
 * `useMutationState`, which puts `variables` on a RENDER path: a selector
 * walking every in-flight write on every notification. A multi-megabyte string
 * there would have been wrong independently of gc, so it was a precondition of
 * that story rather than a fix inside it. The rule it leaves behind:
 * `inFlightPostsOptions`'s `select` returns a FIVE-FIELD PROJECTION and never
 * `mutation.state` or `variables` whole -- no function and no payload reaches a
 * row (pinned in `correspondenceQueries.test.ts`).
 *
 * `gcTime: 0` would not have done it: it cannot reach the observer's
 * `#currentResult`, and neither can `mutationCache.remove()`.
 */
export interface PostMessageVariables {
  threadId: string;
  body: string;
  /**
   * How many files this post carries. The bytes are in the hook's `filesRef`
   * under `localId`; see the warning above for why they are not here.
   */
  fileCount: number;
  /** The pending row this post owns; minted by the hook, never sent. */
  localId: string;
  onProgress?: (percent: number | null) => void;
  /** Handed the abort handle BEFORE the first byte goes out (AC 11, FR18). */
  onUploadStarted?: (abort: () => void) => void;
}

/** What an edit carries. */
export interface EditMessageVariables {
  threadId: string;
  messageId: string;
  body: string;
  /** Inline images ADDED by the edit (#1530); absent when none were. */
  files?: OutgoingMessageFile[];
}

/** What a withdrawal carries. */
export interface DeleteMessageVariables {
  threadId: string;
  messageId: string;
}

/**
 * What a rename carries (Story 7.3). The title is a short string, so none of
 * `PostMessageVariables`' retention worries apply.
 */
export interface RenameThreadVariables {
  threadId: string;
  title: string;
}

/** The three non-default options, with their reasons in the header above. */
const WRITE_OPTIONS = {
  // ⚠️ NOT the app client's "retry once on non-4xx"; there is no idempotency
  // key, so a committed-then-timed-out POST would be delivered twice (1).
  retry: false as const,
  // ⚠️ NOT the default 'online'; a paused mutation is a replay path (2).
  networkMode: 'always' as const,
  meta: {
    // The panel owns its line; see the threads section's header.
    errorMessage: undefined,
  },
};

export const createThreadMutation = (args: { provider: IMessagingProvider }) =>
  mutationOptions({
    mutationKey: correspondenceMutationKeys.createThread(),
    // ⚠️ NO MEMBER LIST (#1069 P1.1): the server projects membership from the
    // request team and ignores anything a client sends. ONE CALL (Story 1.3,
    // D1): the conversation and its first message are created together, so
    // there is no partial failure to handle.
    // Read off `args.provider` at CALL time, never destructured above.
    mutationFn: (v: CreateThreadVariables): Promise<Thread> =>
      args.provider.createThread(
        v.requestId,
        v.space,
        v.title,
        v.body,
        v.files,
      ),
    ...WRITE_OPTIONS,
  });

export const postMessageMutation = (args: {
  provider: IMessagingProvider;
  /**
   * The bytes for one post, read at CALL time from the overlay for the reason
   * `PostMessageVariables` gives. Read off `args` at call time and never
   * destructured above, exactly as `provider` is.
   *
   * ⚠️ IT CONSUMES THEM (#1069 Story 6.6 code review, decision 2). The overlay
   * deletes the payload as it hands it over, so this is a take rather than a
   * get and there is exactly one caller, once, below. See `uploadOverlay.takeFiles`.
   */
  takeFiles: (localId: string) => OutgoingMessageFile[] | undefined;
}) =>
  mutationOptions({
    mutationKey: correspondenceMutationKeys.post(),
    mutationFn: (v: PostMessageVariables): Promise<Message> => {
      if (v.fileCount === 0) {
        return args.provider.postMessage(v.threadId, v.body);
      }
      const files = args.takeFiles(v.localId);
      // The entry is written before `mutate()` and the bytes are consumed here,
      // once, so an absent payload at this point is a programming error rather
      // than a user-reachable state. (Story 6.6's code review made that claim
      // briefly false: colliding `localId`s let one post's `forget` delete
      // another's bytes. The id is module-scoped now, which is what restores
      // it.) It fails loudly rather than silently posting the text without its
      // files, which would look like a successful send.
      if (!files || files.length === 0) {
        return Promise.reject(
          new Error(
            `postMessageMutation: no staged files for ${v.localId}; the hook's filesRef was cleared before the call`,
          ),
        );
      }
      const upload = args.provider.postMessageWithFiles!({
        threadId: v.threadId,
        body: v.body,
        files,
        onProgress: v.onProgress,
      });
      // ⚠️ BEFORE THE PROMISE IS RETURNED, NOT AFTER IT SETTLES (AC 11,
      // FR18): `cancelPending` has to be able to reach the handle in the first
      // moments, and `apiProvider.ts:281-283` resolves its headers lazily
      // inside the promise for exactly that reason.
      v.onUploadStarted?.(upload.abort);
      return upload.promise;
    },
    ...WRITE_OPTIONS,
  });

export const editMessageMutation = (args: { provider: IMessagingProvider }) =>
  mutationOptions({
    mutationKey: correspondenceMutationKeys.edit(),
    mutationFn: (v: EditMessageVariables): Promise<Message> =>
      args.provider.editMessage(v.threadId, v.messageId, v.body, v.files),
    ...WRITE_OPTIONS,
  });

export const deleteMessageMutation = (args: { provider: IMessagingProvider }) =>
  mutationOptions({
    mutationKey: correspondenceMutationKeys.delete(),
    mutationFn: (v: DeleteMessageVariables): Promise<void> =>
      args.provider.deleteMessage(v.threadId, v.messageId),
    ...WRITE_OPTIONS,
  });

/**
 * The fifth writer (Story 7.3, FR74), under every rule in this file's header:
 * no retry, `networkMode: 'always'`, no thread id in the key, and the provider
 * read at CALL time from `args` so a re-provisioned provider is picked up.
 */
export const renameThreadMutation = (args: { provider: IMessagingProvider }) =>
  mutationOptions({
    mutationKey: correspondenceMutationKeys.renameThread(),
    mutationFn: (v: RenameThreadVariables): Promise<Thread> =>
      args.provider.renameThread(v.threadId, v.title),
    ...WRITE_OPTIONS,
  });

// ---------------------------------------------------------------------------
// The pending rows (#1069 Story 6.6). Which posts are in flight is the cache's;
// how far along each one is, is the overlay's. These two functions are the
// join, and both are pure so the hook holds no copy of either.
// ---------------------------------------------------------------------------

/**
 * One in-flight post as the MUTATION CACHE knows it, projected.
 *
 * ⚠️ A PROJECTION, NEVER `mutation.state` OR `variables` WHOLE. A `select`
 * returning either would put the two callbacks on a render path -- and, before
 * Story 6.5's code review moved them out, 54 MiB of base64 with them (see
 * `PostMessageVariables`). Five plain fields is everything a row needs.
 */
export interface InFlightPost {
  localId: string;
  threadId: string;
  body: string;
  fileCount: number;
  /**
   * `mutation.state.submittedAt`, a millisecond stamp.
   *
   * ⚠️ IT IS STAMPED TWICE PER POST AND THE SECOND ONE WINS (measured,
   * `mutation.js:215-227` with `:96-107`): the `pending` reducer runs once
   * before `onMutate` and again after it resolves with the context, one
   * microtask later. Nothing renders in between -- the observer's notification
   * is a macrotask away -- so the drift is recorded, not guarded.
   */
  submittedAt: number;
}

/**
 * The `useMutationState` options that read every post still on the wire.
 *
 * ⚠️ NO THREAD FILTER IN HERE, AND THAT IS NOT A STYLE CHOICE (Story 6.6, C1).
 * `useMutationState` computes its result at mount and thereafter ONLY inside
 * its `mutationCache.subscribe` callback (`useMutationState.js:20-46`); the
 * snapshot getter just returns the last one. A thread switch fires no mutation
 * event, so a `predicate` or a `select` reading the open thread would leave
 * thread A's rows sitting under thread B's header until some unrelated write
 * happened to notify. The open-thread filter is applied in RENDER, by
 * `toPendingRows` below.
 *
 * `exact` is left default: the key has no suffix, so partial and exact match
 * the same set.
 */
export const inFlightPostsOptions = () => ({
  filters: {
    mutationKey: correspondenceMutationKeys.post(),
    status: 'pending' as const,
  },
  /**
   * ⚠️ IT GUARDS THE SHAPE, BECAUSE A THROW HERE IS NOT LOCAL (#1069 Story 6.6
   * code review). This runs inside `mutationCache.subscribe`'s callback
   * (`useMutationState.js:29-34`), so an exception propagates into the cache's
   * notify loop and takes down every OTHER subscriber's update too, not just
   * this hook's. `variables` is optional on a restored or dehydrated mutation
   * (`mutation.js:57-58`) and the key is matched partially, so any future
   * mutation registered under `['correspondence','write','post']` with a
   * different variables shape would otherwise be a panel-wide outage rather
   * than a local bug. A row that does not conform is dropped by `toPendingRows`.
   */
  select: (mutation: Mutation): InFlightPost | null => {
    const v = mutation.state.variables as PostMessageVariables | undefined;
    if (typeof v?.localId !== 'string' || typeof v.threadId !== 'string') {
      return null;
    }
    return {
      localId: v.localId,
      threadId: v.threadId,
      body: v.body,
      fileCount: v.fileCount,
      submittedAt: mutation.state.submittedAt,
    };
  },
});

/**
 * The join: what the cache holds, plus what it cannot, filtered to the thread
 * the user is looking at, in the order the posts were issued.
 *
 * ⚠️ THE OVERLAY ENTRY'S PRESENCE IS THE IN-FLIGHT FACT, NOT THE MUTATION'S
 * `status` (#1069 Story 6.6 code review, decision 1). `status` leaves `pending`
 * only at `mutation.js:144`, which runs AFTER `await options.onSuccess` -- and
 * this hook's `onSuccess` appends the server message and then awaits a network
 * re-fetch. Keying the row's life on `status` therefore showed the message
 * twice, the second copy with its bar rewound to 0%, for the whole re-fetch
 * (measured: `messages` already carried "with a file" while `pending` still
 * carried it at `percent: 0`, from 99 a tick earlier). Reading
 * `mutation.state.data` instead does not help: that field is SET BY the same
 * dispatch. So the signal is ours -- the wrapper seeds an entry before
 * `mutate()` for EVERY post, and `forget` on each exit path retires the row in
 * the same tick as the append, which is exactly when the deleted
 * `removePending` used to run.
 *
 * ⚠️ THE FILE KEYS ARE ABSENT, NOT ZERO, ON A TEXT ROW (Story 4.1, AC 8): the
 * row branches on their presence.
 */
export const toPendingRows = (
  rows: readonly (InFlightPost | null)[],
  openThreadId: string | null,
  overlay: ReadonlyMap<string, UploadOverlay>,
): PendingMessage[] =>
  rows
    .filter(
      (row): row is InFlightPost =>
        row !== null &&
        row.threadId === openThreadId &&
        overlay.has(row.localId),
    )
    .slice()
    .sort((a, b) => a.submittedAt - b.submittedAt)
    .map(row => {
      const base: PendingMessage = {
        localId: row.localId,
        threadId: row.threadId,
        body: row.body,
        sentAt: new Date(row.submittedAt).toISOString(),
      };
      if (row.fileCount <= 0) return base;
      // Present, because the filter above kept only rows that have one.
      const entry = overlay.get(row.localId) as UploadOverlay;
      return {
        ...base,
        fileCount: row.fileCount,
        percent: entry.percent,
        slow: entry.slow,
      };
    });
